import React from 'react';
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";
import { openSubscribe } from "../../../Utils/misc/openLink.ts";
import { submitFeedback } from "../Settings/SettingsContent/Feedback/submitFeedback.ts";
import { PRO_FEATURES } from "../UpgradeFeatureBreakdown/upgradeFeatures.ts";

interface DowngradeMessageProps {}

const DowngradeMessage: React.FC<DowngradeMessageProps> = () => {
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const userObj = useAppStore((state: any) => state.userObj);
    const { environment, vscode } = environmentObj;
    const { user } = userObj;
    const popupRef = React.useRef<HTMLDivElement>(null);

    const closePopup = () => {
        setDynamicStateObj({ visiblePopup: null });
    };

    const handleUpgradeClick = async (event: React.MouseEvent) => {
        submitFeedback(user, `User clicked upgrade from downgrade message popup in ${environment}`);
        closePopup();
        openSubscribe(event, environment, vscode);
    };

    // Handle clicking outside the popup to close it
    React.useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                closePopup();
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
                ref={popupRef}
                className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-lg w-full mx-4 relative"
                role="dialog"
                aria-modal="true"
                aria-labelledby="downgrade-modal-title"
            >
                <div className="p-8 text-center">
                    {/* Close Button */}
                    <button
                        onClick={closePopup}
                        className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-xl font-bold"
                        aria-label="Close downgrade message"
                    >
                        ×
                    </button>

                    {/* Icon */}
                    <div className="mb-6">
                        <div className="w-16 h-16 mx-auto bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 18.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                    </div>

                    {/* Header */}
                    <h2 id="downgrade-modal-title" className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                        You've reached your free limit
                    </h2>

                    {/* Message */}
                    <p className="text-neutral-600 dark:text-neutral-300 mb-8 leading-relaxed">
                        Thanks for trying Onuro! You've used up your free messages.
                        To continue using advanced features and unlimited messages, upgrade to Pro.
                    </p>

                    {/* Benefits */}
                    <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-6 mb-8 text-left">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4 text-center">
                            Pro Plan Benefits:
                        </h3>
                        <ul className="space-y-2">
                            {PRO_FEATURES.map((feature, index) => (
                                <li key={index} className="flex items-center">
                                    <span className="text-cyan-500 mr-3 text-sm">✓</span>
                                    <span className="text-neutral-600 dark:text-neutral-300 text-sm">
                                        {feature}
                                    </span>
                                </li>
                            ))}
                        </ul>

                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center">
                        <button
                            onClick={handleUpgradeClick}
                            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center"
                            aria-label="Upgrade to Pro plan"
                        >
                            ✨ Upgrade to Pro
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DowngradeMessage;